import { useCallback, useEffect, useRef } from 'react';
import {
  Controller,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { Checkbox, CheckboxProps, CheckboxRef } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useAdditionalFormContext } from '../AdditionalFormContext';
import { useGetIdFromName } from '../utils';

export interface FormCheckboxProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData>
> extends Omit<CheckboxProps, 'id' | 'onBlur'> {
  id?: string;
  name: Name;
  onBlur?: (event: FocusEvent) => void;
}

export const FormCheckbox = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData>
>(
  props: FormCheckboxProps<FormData, Name>
) => {
  const getIdFromName = useGetIdFromName();
  const { name, id = getIdFromName(name), onBlur, ...rest } = props;
  const { control } = useFormContext<FormData>();
  const { onChange: formOnChange } = useAdditionalFormContext();

  const ref = useRef<CheckboxRef>(undefined);
  const handleCheckboxRef = useCallback((checkboxRef: CheckboxRef) => {
    ref.current = checkboxRef;
  }, []);

  useEffect(() => {
    const input = ref.current?.input;
    if (input && onBlur) {
      input.addEventListener('blur', onBlur);
      return () => input.removeEventListener('blur', onBlur);
    }
  }, [onBlur]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const { value, disabled } = field;
        const onChange = (event: CheckboxChangeEvent) => {
          rest?.onChange?.(event);
          field.onChange(event);
          formOnChange?.();
        };
        return (
          <Checkbox
            ref={handleCheckboxRef}
            disabled={disabled}
            {...rest}
            id={id}
            checked={value}
            onChange={onChange}
          />
        );
      }}
    />
  );
};
